import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  FileText, FilePlus2, ArrowLeft, Trash2, Warehouse, Upload,
  Loader2, CheckCircle2, AlertTriangle, Package, FileCode, Lightbulb,
  TrendingUp, TrendingDown, DollarSign,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { enterAbrePrimeiro } from '@/lib/keyboard'

interface Deposito {
  id: number; nome: string; codigo: string; tipo: string;
  is_default: boolean; permite_venda: boolean; ativo: boolean;
}
interface Produto {
  id: number; codigo: string; descricao: string; unidade: string;
  preco_custo: number; preco_venda: number;
}
interface ContaGerencial {
  id: number; codigo: string; nome: string; tipo: string;
}
interface ItemEntrada {
  produto_id: number | ''
  produto_codigo?: string
  produto_descricao?: string
  quantidade: number
  informar_lote: boolean
  lote_codigo: string
  validade: string
  endereco: string
  custo_unitario: number
}

export default function EntradaEstoquePage() {
  const nav = useNavigate()
  const [tab, setTab] = useState<'manual' | 'simples' | 'xml' | 'historico'>('manual')

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900 flex items-center gap-2">
            <FilePlus2 size={22} /> Entrada de Estoque
          </h1>
          <p className="text-sm text-ink-500 mt-0.5">
            Lançamento de NF de compra manual ou importação de XML. Gera estoque, lotes e contas a pagar.
          </p>
        </div>
        <button onClick={() => nav('/estoque')} className="btn-ghost">
          <ArrowLeft size={14} /> Voltar
        </button>
      </div>

      <div className="flex gap-1 border-b border-ink-200">
        {[
          { id: 'manual', label: 'NF Manual', icon: FileText },
          { id: 'simples', label: 'Sem nota', icon: FilePlus2 },
          { id: 'xml', label: 'Importar XML', icon: FileCode },
          { id: 'historico', label: 'Histórico', icon: Package },
        ].map(t => (
          <button key={t.id}
            onClick={() => setTab(t.id as any)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-brand-600 text-brand-700'
                : 'border-transparent text-ink-500 hover:text-ink-800'
            }`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'manual' && <EntradaManual />}
      {tab === 'simples' && <EntradaManual semNota />}
      {tab === 'xml' && <ImportarXML />}
      {tab === 'historico' && <HistoricoNFs />}
    </div>
  )
}

// ============================================================
// ENTRADA MANUAL
// ============================================================
function EntradaManual({ semNota = false }: { semNota?: boolean }) {
  const qc = useQueryClient()
  const [fornecedorCnpj, setFornecedorCnpj] = useState('')
  const [fornecedorNome, setFornecedorNome] = useState('')
  const [fornecedorId, setFornecedorId] = useState<number | ''>('')
  const [nfNumero, setNfNumero] = useState('')
  const [nfSerie, setNfSerie] = useState('1')
  const [nfChave, setNfChave] = useState('')
  const [nfDataEmissao, setNfDataEmissao] = useState(() => new Date().toISOString().slice(0,10))
  const [nfValorTotal, setNfValorTotal] = useState(0)
  const [depositoId, setDepositoId] = useState<number | ''>('')
  const [contaGerencialId, setContaGerencialId] = useState<number | ''>('')
  const [contaSugerida, setContaSugerida] = useState(false)
  const [observacao, setObservacao] = useState('')
  const [itens, setItens] = useState<ItemEntrada[]>([])
  const [buscaProd, setBuscaProd] = useState('')

  // Pagamento
  const [gerarContasPagar, setGerarContasPagar] = useState(true)
  const [pagParcelas, setPagParcelas] = useState(1)
  const [pagForma, setPagForma] = useState('boleto')
  const [pagDiasPrimeira, setPagDiasPrimeira] = useState(30)
  const [pagPrimeiroVenc, setPagPrimeiroVenc] = useState('')

  // Queries
  const { data: depositos = [] } = useQuery<Deposito[]>({
    queryKey: ['depositos'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })
  const depositosAtivos = useMemo(() => depositos.filter(d => d.ativo), [depositos])

  const { data: fornecedores = [] } = useQuery<any[]>({
    queryKey: ['fornecedores-select'],
    queryFn: async () => (await api.get('/cadastros/pessoas', { params: { tipo: 'fornecedor' } })).data,
  })

  const { data: contasAnaliticas = [] } = useQuery<ContaGerencial[]>({
    queryKey: ['contas-analiticas'],
    queryFn: async () => (await api.get('/plano-contas/analiticas')).data,
  })
  // Ordem inteligente: custos primeiro (mais comum em entrada), depois despesas
  const contasOrdenadas = useMemo(() => {
    const ordem = { custo: 0, despesa_op: 1, despesa_fin: 2, deducao: 3 } as any
    return [...contasAnaliticas].sort((a, b) => {
      const da = ordem[a.tipo] ?? 99
      const db = ordem[b.tipo] ?? 99
      if (da !== db) return da - db
      return a.codigo.localeCompare(b.codigo)
    })
  }, [contasAnaliticas])

  const { data: produtosBusca = [] } = useQuery<Produto[]>({
    queryKey: ['produtos-busca', buscaProd],
    queryFn: async () => (await api.get('/cadastros/produtos', { params: { q: buscaProd } })).data,
    enabled: buscaProd.length >= 2,
  })

  // Default do deposito
  useEffect(() => {
    if (depositosAtivos.length > 0 && depositoId === '') {
      const def = depositosAtivos.find(d => d.is_default) || depositosAtivos[0]
      setDepositoId(def.id)
    }
  }, [depositosAtivos, depositoId])

  // Sugestão de conta gerencial baseada no fornecedor
  useEffect(() => {
    if (!fornecedorId) return
    let cancel = false
    api.get(`/estoque/sugerir-conta-gerencial-fornecedor/${fornecedorId}`)
      .then(r => {
        if (cancel) return
        if (r.data?.conta_gerencial_id) {
          setContaGerencialId(r.data.conta_gerencial_id)
          setContaSugerida(true)
        }
      })
      .catch(() => {})
    return () => { cancel = true }
  }, [fornecedorId])

  function addProduto(p: Produto) {
    setItens(s => [...s, {
      produto_id: p.id,
      produto_codigo: p.codigo,
      produto_descricao: p.descricao,
      quantidade: 1,
      informar_lote: false,
      lote_codigo: '',
      validade: '',
      endereco: '',
      custo_unitario: Number(p.preco_custo) || 0,
    }])
    setBuscaProd('')
  }

  function updItem<K extends keyof ItemEntrada>(idx: number, k: K, v: ItemEntrada[K]) {
    setItens(s => s.map((it, i) => i === idx ? { ...it, [k]: v } : it))
  }

  const totalItens = itens.reduce((s, i) => s + (i.quantidade * i.custo_unitario), 0)

  const submit = useMutation({
    mutationFn: async () => {
      const payload: any = {
        nf_numero: semNota ? 'S/N' : nfNumero,
        nf_serie: semNota ? null : (nfSerie || null),
        nf_chave: semNota ? null : (nfChave ? nfChave.replace(/\D/g, '') : null),
        nf_data_emissao: nfDataEmissao || null,
        nf_valor_total: semNota ? totalItens : nfValorTotal,
        conta_gerencial_id: contaGerencialId,
        observacao: observacao || null,
        deposito_id: depositoId || undefined,
        gerar_contas_pagar: gerarContasPagar,
        pagamento_parcelas: pagParcelas,
        pagamento_forma: pagForma,
        pagamento_dias_primeira: pagDiasPrimeira,
        pagamento_primeiro_vencimento: pagPrimeiroVenc || null,
        itens: itens.map(i => ({
          produto_id: i.produto_id,
          quantidade: i.quantidade,
          informar_lote: i.informar_lote,
          lote_codigo: i.informar_lote ? i.lote_codigo : null,
          validade: i.informar_lote && i.validade ? i.validade : null,
          endereco: i.endereco || null,
          custo_unitario: i.custo_unitario,
        })),
      }
      if (fornecedorId) {
        payload.fornecedor_id = fornecedorId
      } else {
        payload.fornecedor_cnpj = fornecedorCnpj.replace(/\D/g, '')
        payload.fornecedor_nome = fornecedorNome
      }
      return (await api.post('/estoque/nf-entrada-manual', payload)).data
    },
    onSuccess: (r) => {
      const contasMsg = r.contas_pagar_geradas ? ` + ${pagParcelas} parcela(s) a pagar` : ''
      toast.success(`NF #${r.nf_numero} registrada${contasMsg}`)
      qc.invalidateQueries()
      // Limpa
      setNfNumero(''); setNfSerie('1'); setNfChave('')
      setNfValorTotal(0); setObservacao(''); setItens([])
      setFornecedorCnpj(''); setFornecedorNome(''); setFornecedorId('')
      setContaSugerida(false)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao registrar NF'),
  })

  const podeEnviar =
    (fornecedorId || (fornecedorCnpj.replace(/\D/g, '').length >= 11 && fornecedorNome)) &&
    (semNota ? totalItens > 0 : (nfNumero && nfValorTotal > 0)) && contaGerencialId && itens.length > 0 &&
    itens.every(i => i.produto_id && i.quantidade > 0 && (!i.informar_lote || i.lote_codigo))

  return (
    <div className="space-y-4">
      {/* Fornecedor */}
      <div className="card card-body">
        <h3 className="font-semibold mb-3">Fornecedor</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">Fornecedor existente</label>
            <select className="input" value={fornecedorId}
              onChange={e => {
                const id = e.target.value ? Number(e.target.value) : ''
                setFornecedorId(id)
                if (id) { setFornecedorCnpj(''); setFornecedorNome('') }
                else setContaSugerida(false)
              }}>
              <option value="">— ou cadastre um novo →</option>
              {fornecedores.map(f => (
                <option key={f.id} value={f.id}>
                  {f.nome} {f.documento ? `· ${f.documento}` : ''}
                </option>
              ))}
            </select>
          </div>
          {!fornecedorId && (
            <div className="grid grid-cols-[1fr_2fr] gap-2">
              <div>
                <label className="label">CNPJ/CPF</label>
                <input className="input" placeholder="Apenas números"
                  value={fornecedorCnpj} onChange={e => setFornecedorCnpj(e.target.value)} />
              </div>
              <div>
                <label className="label">Nome</label>
                <input className="input" placeholder="Razão social"
                  value={fornecedorNome} onChange={e => setFornecedorNome(e.target.value)} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dados da NF (ou da entrada, quando sem nota) */}
      <div className="card card-body">
        <h3 className="font-semibold mb-3">{semNota ? 'Dados da entrada (sem nota)' : 'Dados da NF'}</h3>
        {semNota && (
          <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
            <FilePlus2 size={16} className="mt-0.5 shrink-0" />
            <span>Entrada de mercadoria <strong>sem nota fiscal</strong>: dá entrada no estoque e gera contas a pagar (pelas parcelas abaixo). O valor é a soma dos itens. Sem nº/série/chave.</span>
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {!semNota && (<>
          <div>
            <label className="label">Número *</label>
            <input className="input" value={nfNumero} onChange={e => setNfNumero(e.target.value)} />
          </div>
          <div>
            <label className="label">Série</label>
            <input className="input" value={nfSerie} onChange={e => setNfSerie(e.target.value)} />
          </div>
          <div className="col-span-2">
            <label className="label">Chave de Acesso</label>
            <input className="input font-mono text-xs" maxLength={54}
              placeholder="44 dígitos (opcional)"
              value={nfChave} onChange={e => setNfChave(e.target.value)} />
          </div>
          </>)}
          <div>
            <label className="label">{semNota ? 'Data da entrada' : 'Emissão'}</label>
            <input type="date" className="input"
              value={nfDataEmissao} onChange={e => setNfDataEmissao(e.target.value)} />
          </div>
          {!semNota && (
          <div>
            <label className="label">Valor Total (R$) *</label>
            <input type="number" step="0.01" className="input"
              value={nfValorTotal} onChange={e => setNfValorTotal(parseFloat(e.target.value) || 0)} />
          </div>
          )}
          <div className="col-span-2">
            <label className="label flex items-center gap-1">
              Conta Gerencial *
              {contaSugerida && contaGerencialId && (
                <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Lightbulb size={10} /> sugerida
                </span>
              )}
            </label>
            <select className="input" value={contaGerencialId}
              onChange={e => { setContaGerencialId(Number(e.target.value)); setContaSugerida(false) }}>
              <option value="">Selecione a classificação contábil...</option>
              {contasOrdenadas.map(c => (
                <option key={c.id} value={c.id}>
                  {c.codigo} — {c.nome} ({c.tipo.replace('_', ' ')})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label flex items-center gap-1"><Warehouse size={12} /> Depósito</label>
            <select className="input" value={depositoId}
              onChange={e => setDepositoId(Number(e.target.value))}>
              {depositosAtivos.map(d => (
                <option key={d.id} value={d.id}>
                  {d.nome}{d.is_default ? ' ★' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="col-span-2 md:col-span-5">
            <label className="label">Observação</label>
            <input className="input" value={observacao} onChange={e => setObservacao(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Itens */}
      <div className="card card-body">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Itens da NF</h3>
          <span className="text-sm text-ink-500">
            Soma: <strong>{formatMoney(totalItens)}</strong>
          </span>
        </div>

        <div className="relative mb-3">
          <input className="input" placeholder="Buscar produto para adicionar..."
            value={buscaProd} onChange={e => setBuscaProd(e.target.value)}
            onKeyDown={enterAbrePrimeiro(produtosBusca.slice(0, 10), p => addProduto(p))} />
          {buscaProd.length >= 2 && produtosBusca.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-ink-200 rounded-md shadow-lg max-h-60 overflow-auto z-10">
              {produtosBusca.slice(0, 10).map(p => (
                <button key={p.id} onClick={() => addProduto(p)}
                  className="w-full text-left px-3 py-2 hover:bg-ink-50 border-b border-ink-100 last:border-0">
                  <div className="text-sm font-medium">{p.descricao}</div>
                  <div className="text-xs text-ink-500">{p.codigo} · Custo atual {formatMoney(p.preco_custo)}</div>
                </button>
              ))}
            </div>
          )}
        </div>

        {itens.length === 0 && (
          <p className="text-sm text-ink-500 text-center py-6">Nenhum item adicionado.</p>
        )}

        {itens.map((it, i) => (
          <div key={i} className="border border-ink-200 rounded-lg p-3 mb-2 bg-ink-50/50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-ink-900">
                {it.produto_codigo} — {it.produto_descricao}
              </span>
              <button onClick={() => setItens(s => s.filter((_, idx) => idx !== i))}
                className="p-1 text-red-600 hover:bg-red-50 rounded">
                <Trash2 size={14} />
              </button>
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
              <div>
                <label className="label-xs">Qtd *</label>
                <input type="number" step="0.001" className="input input-sm"
                  value={it.quantidade}
                  onChange={e => updItem(i, 'quantidade', parseFloat(e.target.value) || 0)} />
              </div>
              <div>
                <label className="label-xs">Custo unit.</label>
                <input type="number" step="0.01" className="input input-sm"
                  value={it.custo_unitario}
                  onChange={e => updItem(i, 'custo_unitario', parseFloat(e.target.value) || 0)} />
              </div>
              <div>
                <label className="label-xs">Endereço</label>
                <input className="input input-sm" placeholder="Ex: A1-03"
                  value={it.endereco}
                  onChange={e => updItem(i, 'endereco', e.target.value)} />
              </div>
              <div className="col-span-3 border-l md:pl-2">
                <label className="flex items-center gap-2 text-xs mb-1">
                  <input type="checkbox" checked={it.informar_lote}
                    onChange={e => updItem(i, 'informar_lote', e.target.checked)} />
                  <span className="font-medium">Informar lote</span>
                </label>
                {it.informar_lote && (
                  <div className="grid grid-cols-2 gap-2">
                    <input className="input input-sm" placeholder="Código"
                      value={it.lote_codigo}
                      onChange={e => updItem(i, 'lote_codigo', e.target.value)} />
                    <input type="date" className="input input-sm"
                      value={it.validade}
                      onChange={e => updItem(i, 'validade', e.target.value)} />
                  </div>
                )}
              </div>
            </div>
            <div className="text-right text-xs text-ink-500 mt-2">
              Subtotal: <strong>{formatMoney(it.quantidade * it.custo_unitario)}</strong>
            </div>
          </div>
        ))}
      </div>

      {/* Pagamento */}
      <div className="card card-body">
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer select-none mb-3">
          <input type="checkbox" checked={gerarContasPagar}
            onChange={e => setGerarContasPagar(e.target.checked)} />
          <span>💰 Gerar contas a pagar desta NF</span>
        </label>
        {gerarContasPagar && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pl-6">
            <div>
              <label className="label">Forma</label>
              <select className="input" value={pagForma} onChange={e => setPagForma(e.target.value)}>
                <option value="boleto">Boleto</option>
                <option value="pix">PIX</option>
                <option value="dinheiro">Dinheiro</option>
                <option value="cartao_credito">Cartão Crédito</option>
                <option value="transferencia">Transferência</option>
              </select>
            </div>
            <div>
              <label className="label">Parcelas</label>
              <input type="number" min={1} max={24} className="input"
                value={pagParcelas} onChange={e => setPagParcelas(parseInt(e.target.value) || 1)} />
            </div>
            <div>
              <label className="label">1ª parcela em (dias)</label>
              <input type="number" min={0} className="input"
                value={pagDiasPrimeira}
                onChange={e => setPagDiasPrimeira(parseInt(e.target.value) || 0)} />
            </div>
            <div>
              <label className="label">Ou data fixa</label>
              <input type="date" className="input"
                value={pagPrimeiroVenc} onChange={e => setPagPrimeiroVenc(e.target.value)} />
            </div>
          </div>
        )}
      </div>

      {/* Submit */}
      <div className="flex items-center justify-end gap-3">
        {Math.abs(totalItens - nfValorTotal) > 0.01 && nfValorTotal > 0 && (
          <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded">
            <AlertTriangle size={12} className="inline" /> Soma dos itens ({formatMoney(totalItens)}) difere do total da NF
          </span>
        )}
        <button disabled={!podeEnviar || submit.isPending}
          onClick={() => submit.mutate()}
          className="btn-primary px-6">
          {submit.isPending ? <><Loader2 size={14} className="animate-spin" /> Registrando...</>
            : <><CheckCircle2 size={14} /> Registrar NF</>}
        </button>
      </div>
    </div>
  )
}

// ============================================================
// IMPORTAR XML
// ============================================================
function ImportarXML() {
  const qc = useQueryClient()
  const [xml, setXml] = useState('')
  const [depositoId, setDepositoId] = useState<number | ''>('')
  const [contaGerencialId, setContaGerencialId] = useState<number | ''>('')
  const [autoCriar, setAutoCriar] = useState(true)
  const [gerarContasPagar, setGerarContasPagar] = useState(true)
  const [pagParcelas, setPagParcelas] = useState(1)
  const [pagForma, setPagForma] = useState('boleto')
  const [pagDiasPrimeira, setPagDiasPrimeira] = useState(30)
  const [resultado, setResultado] = useState<any>(null)

  const { data: depositos = [] } = useQuery<Deposito[]>({
    queryKey: ['depositos'],
    queryFn: async () => (await api.get('/estoque/depositos')).data,
  })
  const depositosAtivos = depositos.filter(d => d.ativo)

  const { data: contasAnaliticas = [] } = useQuery<ContaGerencial[]>({
    queryKey: ['contas-analiticas'],
    queryFn: async () => (await api.get('/plano-contas/analiticas')).data,
  })
  const contasOrdenadas = useMemo(() => {
    const ordem = { custo: 0, despesa_op: 1, despesa_fin: 2, deducao: 3 } as any
    return [...contasAnaliticas].sort((a, b) => {
      const da = ordem[a.tipo] ?? 99
      const db = ordem[b.tipo] ?? 99
      if (da !== db) return da - db
      return a.codigo.localeCompare(b.codigo)
    })
  }, [contasAnaliticas])

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => setXml(reader.result as string)
    reader.readAsText(file)
  }

  const importar = useMutation({
    mutationFn: async () => (await api.post('/estoque/importar-xml', {
      xml_conteudo: xml,
      conta_gerencial_id: contaGerencialId,
      deposito_id: depositoId || undefined,
      auto_criar_produtos: autoCriar,
      gerar_contas_pagar: gerarContasPagar,
      pagamento_parcelas: pagParcelas,
      pagamento_forma: pagForma,
      pagamento_dias_primeira: pagDiasPrimeira,
    })).data,
    onSuccess: (r) => {
      toast.success(`NF ${r.nf_numero} importada — ${r.itens_processados.length} item(ns)`)
      setResultado(r); qc.invalidateQueries(); setXml('')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao importar XML'),
  })

  const podeImportar = xml.trim() && contaGerencialId

  return (
    <div className="space-y-4">
      <div className="card card-body space-y-3">
        <h3 className="font-semibold">Importar XML de NFe</h3>

        <div>
          <label className="label">Arquivo XML</label>
          <input type="file" accept=".xml,text/xml" onChange={handleFileUpload}
            className="block text-sm text-ink-600 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-brand-100 file:text-brand-700 file:text-sm file:font-medium hover:file:bg-brand-200" />
        </div>

        <div>
          <label className="label">…ou cole o XML aqui</label>
          <textarea className="input font-mono text-xs" rows={6} value={xml}
            onChange={e => setXml(e.target.value)}
            placeholder="<?xml version='1.0'?>..." />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Conta Gerencial *</label>
            <select className="input" value={contaGerencialId}
              onChange={e => setContaGerencialId(Number(e.target.value))}>
              <option value="">Selecione...</option>
              {contasOrdenadas.map(c => (
                <option key={c.id} value={c.id}>
                  {c.codigo} — {c.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label flex items-center gap-1"><Warehouse size={12} /> Depósito</label>
            <select className="input" value={depositoId}
              onChange={e => setDepositoId(e.target.value ? Number(e.target.value) : '')}>
              <option value="">Default da empresa</option>
              {depositosAtivos.map(d => (
                <option key={d.id} value={d.id}>{d.nome}{d.is_default ? ' ★' : ''}</option>
              ))}
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={autoCriar} onChange={e => setAutoCriar(e.target.checked)} />
          <span><strong>Auto-criar produtos</strong> que não existirem no ERP</span>
        </label>
      </div>

      <div className="card card-body">
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer mb-3">
          <input type="checkbox" checked={gerarContasPagar}
            onChange={e => setGerarContasPagar(e.target.checked)} />
          <span>💰 Gerar contas a pagar</span>
        </label>
        {gerarContasPagar && (
          <div className="grid grid-cols-3 gap-3 pl-6">
            <div>
              <label className="label">Forma</label>
              <select className="input" value={pagForma} onChange={e => setPagForma(e.target.value)}>
                <option value="boleto">Boleto</option>
                <option value="pix">PIX</option>
                <option value="dinheiro">Dinheiro</option>
                <option value="transferencia">Transferência</option>
              </select>
            </div>
            <div>
              <label className="label">Parcelas</label>
              <input type="number" min={1} max={24} className="input" value={pagParcelas}
                onChange={e => setPagParcelas(parseInt(e.target.value) || 1)} />
            </div>
            <div>
              <label className="label">1ª em (dias)</label>
              <input type="number" min={0} className="input" value={pagDiasPrimeira}
                onChange={e => setPagDiasPrimeira(parseInt(e.target.value) || 0)} />
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end">
        <button disabled={!podeImportar || importar.isPending}
          onClick={() => importar.mutate()}
          className="btn-primary px-6">
          {importar.isPending ? <><Loader2 size={14} className="animate-spin" /> Importando...</>
            : <><Upload size={14} /> Importar XML</>}
        </button>
      </div>

      {resultado && (
        <div className="space-y-3">
          <div className="card card-body bg-emerald-50 border-emerald-200">
            <h4 className="font-semibold text-emerald-900 flex items-center gap-2">
              <CheckCircle2 size={16} /> NF {resultado.nf_numero} importada
            </h4>
            <div className="text-sm text-emerald-800 mt-2 space-y-1">
              <div>Fornecedor: <strong>{resultado.fornecedor_nome}</strong></div>
              <div>Valor total: <strong>{formatMoney(resultado.valor_total)}</strong></div>
              <div>Itens processados: <strong>{resultado.itens_processados.length}</strong></div>
              {resultado.contas_pagar_geradas && <div className="flex items-center gap-1"><DollarSign size={12} /> Contas a pagar geradas</div>}
              {resultado.itens_sem_match?.length > 0 && (
                <div className="text-amber-700 mt-2">
                  <AlertTriangle size={12} className="inline" /> {resultado.itens_sem_match.length} item(ns) sem match
                </div>
              )}
            </div>
          </div>

          {resultado.alertas?.length > 0 && (
            <div className="card card-body bg-amber-50 border-amber-300">
              <h4 className="font-semibold text-amber-900 flex items-center gap-2">
                <TrendingUp size={16} /> Alertas de divergência de preço
              </h4>
              <div className="mt-2 space-y-2">
                {resultado.alertas.map((a: any, i: number) => {
                  const subiu = a.variacao_pct > 0
                  return (
                    <div key={i} className="text-sm flex items-center justify-between gap-3 border-t border-amber-200 pt-2 first:border-t-0 first:pt-0">
                      <div>
                        <div className="font-medium text-amber-900">{a.produto_codigo} — {a.produto_descricao}</div>
                        <div className="text-amber-700 text-xs">
                          {formatMoney(a.preco_anterior)} → {formatMoney(a.preco_atual)}
                        </div>
                      </div>
                      <div className={`text-sm font-bold flex items-center gap-1 ${subiu ? 'text-red-700' : 'text-green-700'}`}>
                        {subiu ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                        {subiu ? '+' : ''}{a.variacao_pct.toFixed(1)}%
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {resultado.itens_processados?.length > 0 && (
            <div className="card overflow-hidden">
              <div className="px-4 py-2 bg-ink-50 border-b border-ink-200 text-xs font-semibold text-ink-700 uppercase tracking-wide">
                Itens processados — custo com rateio aplicado
              </div>
              <table className="table text-sm">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Descrição</th>
                    <th className="text-right">Qtd</th>
                    <th className="text-right">Custo unit.</th>
                    <th className="text-right">Frete rat.</th>
                    <th className="text-right">Custo médio</th>
                  </tr>
                </thead>
                <tbody>
                  {resultado.itens_processados.map((it: any, i: number) => (
                    <tr key={i}>
                      <td className="font-mono text-xs">{it.produto_codigo}</td>
                      <td>{it.produto_descricao}</td>
                      <td className="text-right">{it.quantidade}</td>
                      <td className="text-right">{formatMoney(it.custo_unitario)}</td>
                      <td className="text-right text-ink-500">{formatMoney(it.frete_rateado || 0)}</td>
                      <td className="text-right font-semibold">{formatMoney(it.custo_medio_atualizado || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================
// HISTÓRICO
// ============================================================
function HistoricoNFs() {
  const { data: nfs = [], isLoading } = useQuery<any[]>({
    queryKey: ['nf-entradas'],
    queryFn: async () => (await api.get('/estoque/nf-entradas')).data,
  })

  return (
    <div className="card overflow-hidden">
      <table className="table">
        <thead>
          <tr>
            <th>NF</th><th>Série</th><th>Fornecedor</th>
            <th>Emissão</th><th className="text-right">Valor</th>
            <th>Status</th><th>Chave</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && <tr><td colSpan={7} className="text-center py-6 text-ink-400">Carregando...</td></tr>}
          {!isLoading && nfs.length === 0 &&
            <tr><td colSpan={7} className="text-center py-6 text-ink-400">Nenhuma NF de entrada</td></tr>}
          {nfs.map(n => (
            <tr key={n.id}>
              <td className="font-medium">{n.numero}</td>
              <td>{n.serie || '—'}</td>
              <td>{n.fornecedor_nome || '—'}</td>
              <td className="text-xs">{n.emitido_em ? new Date(n.emitido_em).toLocaleDateString('pt-BR') : '—'}</td>
              <td className="text-right">{formatMoney(n.valor_total)}</td>
              <td>
                <span className={`text-xs px-2 py-0.5 rounded ${
                  n.status === 'autorizado' ? 'bg-emerald-100 text-emerald-700' : 'bg-ink-100 text-ink-600'
                }`}>{n.status}</span>
              </td>
              <td className="text-xs font-mono text-ink-500">
                {n.chave ? `${n.chave.slice(0, 8)}…${n.chave.slice(-4)}` : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
